/**
 * 首屏形变引擎 —— Canvas 2D 手写，零依赖。
 *
 * 为什么不是 three.js / SVG：这是个 2D 形变问题。Canvas 2D 的细线最锐利、
 * 包体零成本；WebGL 的 LineSegments 线宽不可控、抗锯齿差；SVG 要维护 270 个
 * 动画 DOM 节点，移动端必卡。
 *
 * 三态之外还各叠一层「仪器读数」——关节角度弧、节点延迟、回撤标注。
 * 这层是这个首屏可信度的来源：它不是装饰，它在真的算东西。
 */
import {
  N, BONE, AMBER, EMBER, CHANNEL_COLORS, rgba, type RGB, type Pt,
  BONES, poseJoints, fillPose, poseT, poseE,
  graphT, graphE, gNodes, gLinks,
  quantT, quantE, quantEbar, CN, peakI, troughI, MDDPCT,
} from './rigs';

const MONO = '"SF Mono",ui-monospace,Menlo,monospace';

/** 右下角规格读数：每个通道一组，仿仪器侧铭牌 */
const READOUTS: [string, string][][] = [
  [['RIG', 'SKELETON · 15 JOINT'], ['INPUT', 'SINGLE RGB CAMERA'], ['RATE', '30 FPS · ON-DEVICE'], ['LAT', '11 ms']],
  [['GRAPH', '12 NODES · 18 EDGES'], ['RUNTIME', '7×24 AUTONOMOUS'], ['QUEUE', '3 PENDING'], ['LAT', '240 ms']],
  [['SERIES', 'EQUITY · DAILY'], ['SIGNAL', 'TREND + VOL TARGET'], ['MDD', MDDPCT], ['SHARPE', '1.24']],
];

export type MorphHandle = {
  setChannel(i: number): void;
  /** 悬停锁定：锁住时停止自动轮播 */
  setLocked(v: boolean): void;
  destroy(): void;
};

export function createMorphEngine(
  cv: HTMLCanvasElement,
  opts: {
    stage?: HTMLElement;
    /**
     * 正文栏的量尺元素。画布是铺满视口的，正文却是 `mx-auto max-w-[1180px]` 居中的，
     * 所以图形和右下角铭牌不能按视口边算——屏幕一宽就和文案各跑一边。量尺是一个
     * 和正文同宽的空 div，每次 resize 读它的矩形，容器宽度以后怎么改都自动跟上。
     */
    gutter?: HTMLElement | null;
    onChannel?: (i: number) => void;
    autoMs?: number;
  } = {},
): MorphHandle {
  const ctx = cv.getContext('2d')!;
  const stage = opts.stage ?? cv.parentElement!;
  const autoSec = (opts.autoMs ?? 6500) / 1000;
  const reduced =
    typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

  let W = 0, H = 0;
  /** 正文栏在画布坐标系里的左右边界 */
  let CL = 0, CR = 0;
  const DPR = Math.min(devicePixelRatio || 1, 2);

  const P: Pt[] = Array.from({ length: N }, () => ({ x: 0, y: 0 }));
  let w = [1, 0, 0];
  let target = 0;
  let tAuto = 0;
  let locked = false;
  let inited = false;
  let ghosts: Pt[][] = [];
  const mouse = { x: 1e9, y: 1e9, in: false };

  function resize() {
    const r = cv.getBoundingClientRect();
    // 量尺要先读：下面那道守卫会在尺寸没变时提前返回，边界读取不能被它挡掉。
    const g = opts.gutter?.getBoundingClientRect();
    CL = g ? g.left - r.left : 24;
    CR = g ? g.right - r.left : r.width - 24;

    // 尺寸没变就直接返回：给 canvas.width 赋值会清空画布，而 ResizeObserver
    // 的首次回调晚于首帧——不守这一下，首帧会被静默抹掉。
    if (r.width === W && r.height === H) return;
    W = r.width;
    H = r.height;
    cv.width = W * DPR;
    cv.height = H * DPR;
    ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
  }

  const ro = new ResizeObserver(resize);
  ro.observe(cv);

  const onMove = (e: MouseEvent) => {
    const r = cv.getBoundingClientRect();
    mouse.x = e.clientX - r.left;
    mouse.y = e.clientY - r.top;
    mouse.in = true;
  };
  const onLeave = () => { mouse.in = false; };
  stage.addEventListener('mousemove', onMove);
  stage.addEventListener('mouseleave', onLeave);

  /* ── 绘制助手 ─────────────────────────────────────────── */

  /** 四角包围框：比整框更像取景器 */
  function bracket(x: number, y: number, w2: number, h2: number, a: number, c: RGB) {
    const L = 11;
    ctx.strokeStyle = rgba(c, a);
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x - w2, y - h2 + L); ctx.lineTo(x - w2, y - h2); ctx.lineTo(x - w2 + L, y - h2);
    ctx.moveTo(x + w2 - L, y - h2); ctx.lineTo(x + w2, y - h2); ctx.lineTo(x + w2, y - h2 + L);
    ctx.moveTo(x + w2, y + h2 - L); ctx.lineTo(x + w2, y + h2); ctx.lineTo(x + w2 - L, y + h2);
    ctx.moveTo(x - w2 + L, y + h2); ctx.lineTo(x - w2, y + h2); ctx.lineTo(x - w2, y + h2 - L);
    ctx.stroke();
  }

  /** 关节角度弧。只画弧和两条边，度数由调用方渲染到固定读数列里 */
  function angleArc(A: Pt, B: Pt, Cc: Pt, a: number, col: RGB, R: number) {
    const a1 = Math.atan2(A.y - B.y, A.x - B.x);
    const a2 = Math.atan2(Cc.y - B.y, Cc.x - B.x);
    let d = a2 - a1;
    while (d > Math.PI) d -= 6.28318;
    while (d < -Math.PI) d += 6.28318;
    ctx.strokeStyle = rgba(col, a * 0.85);
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.arc(B.x, B.y, R, a1, a1 + d, d < 0);
    ctx.stroke();
    ctx.strokeStyle = rgba(col, a * 0.3);
    ctx.beginPath();
    ctx.moveTo(B.x, B.y); ctx.lineTo(B.x + Math.cos(a1) * R, B.y + Math.sin(a1) * R);
    ctx.moveTo(B.x, B.y); ctx.lineTo(B.x + Math.cos(a2) * R, B.y + Math.sin(a2) * R);
    ctx.stroke();
    return Math.abs(d * 57.2958);
  }

  function label(x: number, y: number, s: string, a: number, col: RGB, sz?: number) {
    ctx.font = (sz || 8.5) + 'px ' + MONO;
    ctx.fillStyle = rgba(col, a);
    ctx.fillText(s, x, y);
  }

  /* ── 主循环 ───────────────────────────────────────────── */
  let raf = 0;
  let last = performance.now();

  function draw(now: number) {
    const dt = Math.min((now - last) / 1000, 0.05);
    last = now;
    const t = now / 1000;

    if (!locked && !reduced) {
      tAuto += dt;
      if (tAuto > autoSec) { tAuto = 0; setChannel((target + 1) % 3); }
    }
    for (let i = 0; i < 3; i++) w[i] += ((i === target ? 1 : 0) - w[i]) * Math.min(1, dt * 4.2);
    const sum = w[0] + w[1] + w[2] || 1;
    const wn = [w[0] / sum, w[1] / sum, w[2] / sum];

    const J = poseJoints(t * 0.52);
    fillPose(J);
    // 窄屏把图形挪到中下方，给上方文案让出整幅宽度；宽屏才是左文右图
    const narrow = W < 760;
    // 一切横向定位都按正文栏宽 CW 算，不按视口宽——超宽屏上图形跟着文案走，
    // 而不是被甩到屏幕边上。
    const CW = CR - CL;
    // 窄屏：文案占上半，图形退到下方三分之一，两者不能重叠
    const scale = narrow ? Math.min(CW * 0.69, H * 0.28) : Math.min(CW * 0.52, H * 0.58);
    const cx = narrow ? (CL + CR) / 2 : CL + CW * 0.715;
    const cy = narrow ? H * 0.74 : H * 0.505;
    const S = (p: Pt) => ({ x: cx + p.x * scale, y: cy + p.y * scale });
    const SJ = J.map(S);

    for (let i = 0; i < N; i++) {
      let tx = poseT[i].x * wn[0] + graphT[i].x * wn[1] + quantT[i].x * wn[2];
      let ty = poseT[i].y * wn[0] + graphT[i].y * wn[1] + quantT[i].y * wn[2];
      tx = cx + tx * scale;
      ty = cy + ty * scale;
      if (mouse.in) {
        // 鼠标是个力场：点被推开再回弹，让画布可触摸而不只是可看
        const dx = tx - mouse.x, dy = ty - mouse.y;
        const d = Math.hypot(dx, dy), R = 130;
        if (d < R && d > 0.01) {
          const f = (1 - d / R) ** 2 * 58;
          tx += (dx / d) * f;
          ty += (dy / d) * f;
        }
      }
      if (!inited) { P[i].x = tx; P[i].y = ty; }
      else { P[i].x += (tx - P[i].x) * 0.14; P[i].y += (ty - P[i].y) * 0.14; }
    }
    inited = true;

    ctx.clearRect(0, 0, W, H);
    ctx.lineCap = 'butt';
    ctx.lineJoin = 'round';
    ctx.textBaseline = 'alphabetic';

    /* 底纹网格 —— 极淡，只为给读数一个坐标感 */
    ctx.strokeStyle = rgba(BONE, 0.022);
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (let x = (W % 72) / 2; x < W; x += 72) { ctx.moveTo(x, 0); ctx.lineTo(x, H); }
    for (let y = (H % 72) / 2; y < H; y += 72) { ctx.moveTo(0, y); ctx.lineTo(W, y); }
    ctx.stroke();

    const mix = [0, 1, 2].map((k) =>
      Math.round(CHANNEL_COLORS[0][k] * wn[0] + CHANNEL_COLORS[1][k] * wn[1] + CHANNEL_COLORS[2][k] * wn[2]),
    ) as unknown as RGB;

    /* ── CH.01 动捕叠加层 ── */
    if (wn[0] > 0.02) {
      const a = wn[0];
      ghosts.push(SJ.map((p) => ({ x: p.x, y: p.y })));
      if (ghosts.length > 26) ghosts.shift();
      for (let g = 0; g < ghosts.length; g += 6) {
        const G = ghosts[g];
        const al = a * 0.055 * (g / ghosts.length);
        ctx.strokeStyle = rgba(BONE, al);
        ctx.lineWidth = 1;
        ctx.beginPath();
        for (const [i, j] of BONES) { ctx.moveTo(G[i].x, G[i].y); ctx.lineTo(G[j].x, G[j].y); }
        ctx.stroke();
      }
      /* 躯干填充：让骨架读起来是个身体，而不是一把棍子 */
      ctx.fillStyle = rgba(BONE, a * 0.045);
      ctx.beginPath();
      ctx.moveTo(SJ[2].x, SJ[2].y); ctx.lineTo(SJ[5].x, SJ[5].y);
      ctx.lineTo(SJ[12].x, SJ[12].y); ctx.lineTo(SJ[9].x, SJ[9].y);
      ctx.closePath(); ctx.fill();

      let x0 = 1e9, x1 = -1e9, y0 = 1e9, y1 = -1e9;
      SJ.forEach((p) => { x0 = Math.min(x0, p.x); x1 = Math.max(x1, p.x); y0 = Math.min(y0, p.y); y1 = Math.max(y1, p.y); });
      y0 = Math.min(y0, SJ[0].y - scale * 0.07);
      const bx = (x0 + x1) / 2, by = (y0 + y1) / 2;
      const bw = (x1 - x0) / 2 + 28, bh = (y1 - y0) / 2 + 18;
      bracket(bx, by, bw, bh, a * 0.45, BONE);
      label(bx - bw, by - bh - 9, 'SUBJECT_01', a * 0.55, BONE);
      // 包围框窄的时候两个标签会挤到一起，宁可少一个
      if (bw * 2 > 190) label(bx + bw - 52, by - bh - 9, 'CONF 0.98', a * 0.55, AMBER);

      ctx.strokeStyle = rgba(BONE, a * 0.85);
      ctx.lineWidth = 1.1;
      ctx.beginPath();
      ctx.arc(SJ[0].x, SJ[0].y - scale * 0.028, scale * 0.04, 0, 6.284);
      ctx.stroke();

      /* 两个关节角就够了，三个开始糊。度数进固定读数列，绝不浮在肢体上 */
      const AR = scale * 0.029;
      const dEl = angleArc(SJ[2], SJ[3], SJ[4], a, AMBER, AR);
      const dKn = angleArc(SJ[9], SJ[10], SJ[11], a, AMBER, AR);
      const lx = bx - bw - 22;
      ([[SJ[3], dEl, 'R_ELBOW'], [SJ[10], dKn, 'R_KNEE']] as [Pt, number, string][]).forEach(
        ([J2, deg, nm], i) => {
          const ly = by - bh + 26 + i * 17;
          ctx.strokeStyle = rgba(AMBER, a * 0.28);
          ctx.lineWidth = 1;
          ctx.beginPath();
          // 斜引线奔向关节高度，末端一小段水平落点——不横穿身体
          ctx.moveTo(lx + 4, ly - 3);
          ctx.lineTo(J2.x - AR - 14, J2.y);
          ctx.lineTo(J2.x - AR - 4, J2.y);
          ctx.stroke();
          ctx.font = '9px ' + MONO;
          ctx.textAlign = 'right';
          ctx.fillStyle = rgba(BONE, a * 0.34);
          ctx.fillText(nm, lx - 46, ly);
          ctx.fillStyle = rgba(AMBER, a * 0.95);
          ctx.fillText(deg.toFixed(1) + '°', lx, ly);
          ctx.textAlign = 'left';
        },
      );

      /* 腕部速度矢量 */
      if (ghosts.length > 8) {
        const prev = ghosts[ghosts.length - 8][4];
        const cur = SJ[4];
        const dx = (cur.x - prev.x) * 2.4, dy = (cur.y - prev.y) * 2.4;
        const m = Math.hypot(dx, dy);
        if (m > 3) {
          ctx.strokeStyle = rgba(EMBER, a * 0.85);
          ctx.lineWidth = 1.2;
          ctx.beginPath();
          ctx.moveTo(cur.x, cur.y); ctx.lineTo(cur.x + dx, cur.y + dy);
          ctx.stroke();
          const ah = Math.atan2(dy, dx);
          ctx.beginPath();
          ctx.moveTo(cur.x + dx, cur.y + dy);
          ctx.lineTo(cur.x + dx - Math.cos(ah - 0.4) * 6, cur.y + dy - Math.sin(ah - 0.4) * 6);
          ctx.moveTo(cur.x + dx, cur.y + dy);
          ctx.lineTo(cur.x + dx - Math.cos(ah + 0.4) * 6, cur.y + dy - Math.sin(ah + 0.4) * 6);
          ctx.stroke();
          label(bx - bw, by + bh + 16, 'WRIST_R', a * 0.34, BONE);
          label(bx - bw + 58, by + bh + 16, (m * 1.6).toFixed(0) + ' px/f', a * 0.75, EMBER);
        }
      }

      /* 地面刻度尺：给画面一个隐含的比例基准 */
      const gy = Math.max(SJ[11].y, SJ[14].y) + 11;
      ctx.strokeStyle = rgba(BONE, a * 0.2);
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(bx - bw - 18, gy); ctx.lineTo(bx + bw + 18, gy);
      for (let i = 0; i <= 16; i++) {
        const x = bx - bw - 18 + ((2 * bw + 36) * i) / 16;
        ctx.moveTo(x, gy); ctx.lineTo(x, gy + (i % 4 === 0 ? 7 : 3.5));
      }
      ctx.stroke();
    }

    /* ── CH.02 智能体叠加层 ── */
    if (wn[1] > 0.02) {
      const a = wn[1];
      gLinks.forEach((L) => {
        const A = S(gNodes[L.a]), B = S(gNodes[L.b]);
        const u = (t * 0.3 + L.ph) % 1;
        const px = A.x + (B.x - A.x) * u, py = A.y + (B.y - A.y) * u;
        ctx.fillStyle = rgba(AMBER, a * 0.95);
        ctx.fillRect(px - 2, py - 2, 4, 4);
        ctx.fillStyle = rgba(AMBER, a * 0.22);
        ctx.fillRect(px - 6, py - 1, 8, 2);
      });
      gNodes.forEach((nd, i) => {
        const p = S(nd);
        ctx.textAlign = 'center';
        ctx.font = '10px "PingFang SC",system-ui,sans-serif';
        ctx.fillStyle = rgba(BONE, a * 0.75);
        ctx.fillText(nd.l, p.x, p.y + 26);
        ctx.font = '8.5px ' + MONO;
        ctx.fillStyle = rgba(AMBER, a * 0.45);
        ctx.fillText(((i * 17) % 90 + 12) + 'ms', p.x, p.y + 37);
        ctx.textAlign = 'left';
      });
    }

    /* ── CH.03 量化叠加层 ── */
    if (wn[2] > 0.02) {
      const a = wn[2];
      ctx.beginPath();
      ctx.moveTo(P[0].x, P[0].y);
      for (let i = 1; i < CN; i++) ctx.lineTo(P[i].x, P[i].y);
      ctx.lineTo(P[CN - 1].x, cy + 0.19 * scale);
      ctx.lineTo(P[0].x, cy + 0.19 * scale);
      ctx.closePath();
      ctx.fillStyle = rgba(EMBER, a * 0.07);
      ctx.fill();

      const ax = cy + 0.19 * scale;
      ctx.strokeStyle = rgba(BONE, a * 0.18);
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 4]);
      ctx.beginPath();
      ctx.moveTo(P[0].x - 14, ax); ctx.lineTo(P[CN - 1].x + 16, ax);
      ctx.stroke();
      ctx.setLineDash([]);
      for (let i = 0; i <= 4; i++) {
        const y = ax - i * 0.085 * scale;
        ctx.strokeStyle = rgba(BONE, a * 0.1);
        ctx.beginPath();
        ctx.moveTo(P[0].x - 14, y); ctx.lineTo(P[0].x - 8, y);
        ctx.stroke();
        label(P[CN - 1].x + 14, y + 3, '+' + i * 10 + '%', a * 0.42, BONE);
      }
      const pk = P[peakI], tr = P[troughI];
      ctx.strokeStyle = rgba(EMBER, a * 0.75);
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(pk.x, pk.y); ctx.lineTo(pk.x, tr.y); ctx.lineTo(tr.x, tr.y);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(pk.x, pk.y, 3, 0, 6.284);
      ctx.moveTo(tr.x + 3, tr.y);
      ctx.arc(tr.x, tr.y, 3, 0, 6.284);
      ctx.stroke();
      ctx.textAlign = 'right';
      ctx.font = '9px ' + MONO;
      ctx.fillStyle = rgba(EMBER, a * 0.95);
      ctx.fillText('MDD ' + MDDPCT, pk.x - 10, (pk.y + tr.y) / 2 + 3);
      ctx.textAlign = 'left';
      label(P[0].x - 14, ax + 18, '2025.01', a * 0.38, BONE);
      label(P[CN - 1].x - 32, ax + 18, '2026.08', a * 0.38, BONE);
    }

    /* ── 点与边 ── */
    const sets: [typeof poseE, number, number][] = [
      [poseE, wn[0], 1.35], [graphE, wn[1], 0.85],
      [quantE, wn[2], 1.0], [quantEbar, wn[2] * 0.42, 1.0],
    ];
    for (const [E, a, lw] of sets) {
      if (a < 0.012) continue;
      ctx.strokeStyle = rgba(mix, 0.42 * a);
      ctx.lineWidth = lw;
      ctx.beginPath();
      for (const [i, j] of E) { ctx.moveTo(P[i].x, P[i].y); ctx.lineTo(P[j].x, P[j].y); }
      ctx.stroke();
    }
    ctx.fillStyle = rgba(mix, 0.85);
    for (let i = 0; i < N; i++) { ctx.beginPath(); ctx.arc(P[i].x, P[i].y, 1.15, 0, 6.284); ctx.fill(); }
    if (wn[0] > 0.3) {
      ctx.strokeStyle = rgba(BONE, wn[0] * 0.9);
      ctx.lineWidth = 1.1;
      SJ.forEach((p) => { ctx.beginPath(); ctx.arc(p.x, p.y, 3.1, 0, 6.284); ctx.stroke(); });
    }

    /* ── 右下角铭牌 ── 窄屏放不下，且会压住通道按钮；规格在各通道板块里已有 */
    if (narrow) { paintChipsHook(); return; }
    const rows = READOUTS[target];
    ctx.font = '8.5px ' + MONO;
    // 右缘对齐正文栏右边界，不是视口右边界
    const rR = CR, rL = CR - 202;
    ctx.strokeStyle = rgba(BONE, 0.1);
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(rL - 14, H - 99); ctx.lineTo(rL - 14, H - 18);
    ctx.stroke();
    rows.forEach((kv, i) => {
      const y = H - 86 + i * 15;
      ctx.textAlign = 'left'; ctx.fillStyle = rgba(BONE, 0.26); ctx.fillText(kv[0], rL, y);
      ctx.textAlign = 'right'; ctx.fillStyle = rgba(BONE, 0.7); ctx.fillText(kv[1], rR, y);
    });
    ctx.textAlign = 'left';
    ctx.fillStyle = rgba(AMBER, 0.5);
    ctx.fillText('T' + (t % 1000).toFixed(2).padStart(7, '0'), rL, H - 24);

    paintChipsHook();
  }

  /** 每帧收尾：接上下一帧 */
  function paintChipsHook() {
    if (!reduced) raf = requestAnimationFrame(draw);
  }

  function setChannel(i: number) {
    if (i === target) return;
    target = i;
    tAuto = 0;
    ghosts = [];
    opts.onChannel?.(i);
  }

  resize();
  if (reduced) {
    // 关掉动效偏好：只画一帧静态图，仍然完整——不是空白降级
    w = [1, 0, 0];
    draw(0);
  } else {
    raf = requestAnimationFrame(draw);
  }

  return {
    setChannel(i) { setChannel(i); if (reduced) { w = [0, 0, 0]; w[i] = 1; inited = false; draw(0); } },
    setLocked(v) { locked = v; },
    destroy() {
      cancelAnimationFrame(raf);
      ro.disconnect();
      stage.removeEventListener('mousemove', onMove);
      stage.removeEventListener('mouseleave', onLeave);
    },
  };
}
